import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Users, Sparkles, Clock } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { earlyBird } from "../lib/earlyBird";
import { EarlyBirdCountdown } from "./LiveInstructors";
import { pick } from "../lib/pick";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.5 },
};

export const DiplomaCard = ({ program, delay = 0 }) => {
  const { t, lang } = useLang();
  const eb = earlyBird(program);
  const loc = lang === "en" ? "en-GB" : "el-GR";

  return (
    <motion.div
      {...fade}
      transition={{ duration: 0.45, delay }}
      className="card-dark !p-0 flex flex-col overflow-hidden group"
      data-testid={`diploma-card-${program.id}`}
    >
      {program.cover && (
        <img
          src={program.cover}
          alt={pick(program, "title", lang)}
          className="w-full h-[170px] object-cover bg-[#EEF4FF] group-hover:scale-[1.03] transition-transform duration-500"
          loading="lazy"
          data-testid={`diploma-cover-${program.id}`}
        />
      )}
      <div className="p-7 flex flex-col flex-1">
      <div className="flex flex-wrap gap-2">
        <span className="badge-dark">{t("live.badge")}</span>
        <span className="badge-lime" data-testid={`diploma-async-${program.id}`}>{t("live.asyncBadge")}</span>
        {eb.active && <span className="badge-outline" data-testid={`diploma-earlybird-${program.id}`}>{t("live.ebBadge")}</span>}
      </div>

      <h3 className="font-heading font-black text-xl mt-5 text-[#0B1B3A] leading-snug">{pick(program, "title", lang)}</h3>
      <p className="text-sm text-[#64748B] mt-3 line-clamp-4 flex-1">{pick(program, "description", lang)}</p>

      <ul className="mt-6 space-y-2.5 text-sm text-[#334155]">
        <li className="flex items-center gap-2.5">
          <CalendarDays className="w-4 h-4 text-[#1D4ED8] shrink-0" />
          {t("live.start")}: {new Date(program.start_date).toLocaleDateString(loc)}
        </li>
        <li className="flex items-center gap-2.5"><Clock className="w-4 h-4 text-[#1D4ED8] shrink-0" /> {t("live.durationV")} · {t("live.calDays")}</li>
        <li className="flex items-center gap-2.5"><Users className="w-4 h-4 text-[#1D4ED8] shrink-0" /> {t("live.seatsV")}</li>
        <li className="flex items-start gap-2.5"><Sparkles className="w-4 h-4 text-[#1D4ED8] shrink-0 mt-0.5" /> {t("live.asyncShort")}</li>
      </ul>

      <div className="mt-6 pt-5 border-t border-[#E2E8F0]">
        {eb.active ? (
          <div className="flex items-end gap-3">
            <p className="font-heading font-black text-3xl text-[#1D4ED8]">{eb.price}€</p>
            <p className="text-base text-[#94A3B8] line-through mb-1">{program.price}€</p>
          </div>
        ) : (
          <p className="font-heading font-black text-3xl text-[#1D4ED8]">{program.price}€</p>
        )}
        <p className="text-xs text-[#64748B] mt-1.5">
          {t("live.payRegistration")} {eb.deposit}€ + {program.installments} {t("live.payInstallments")} {eb.installment}€
        </p>
        {eb.active && <div className="mt-3"><EarlyBirdCountdown until={program.early_bird_until} /></div>}
        <Link to={`/programs/${program.id}`} className="btn-blue w-full mt-5 justify-center" data-testid={`diploma-cta-${program.id}`}>
          {t("diplomas.cta")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      </div>
    </motion.div>
  );
};
